/**
 * Hook that watches for text selections inside a container element and
 * returns the W3C selector + bounding rect whenever the user lifts the mouse
 * or touch after selecting text. Clear it by calling `clear()` or clicking
 * outside the popover.
 */

import { useState, useEffect, useCallback, RefObject } from 'react';
import { captureSelectionFromRange, type Selector } from '@/lib/annotations/selectors';

export interface CapturedSelection {
  selector: Selector;
  rect: DOMRect;
  text: string;
}

export function useSelectionCapture(containerRef: RefObject<HTMLElement>) {
  const [captured, setCaptured] = useState<CapturedSelection | null>(null);

  const clear = useCallback(() => {
    setCaptured(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function onPointerUp() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setCaptured(null);
        return;
      }

      const range = sel.getRangeAt(0);
      // Ignore selections that start or end outside our container
      if (!container!.contains(range.commonAncestorContainer)) {
        setCaptured(null);
        return;
      }

      const selector = captureSelectionFromRange(container!, range);
      if (!selector) { setCaptured(null); return; }

      const rect = range.getBoundingClientRect();
      if (rect.width === 0) { setCaptured(null); return; }

      setCaptured({ selector, rect, text: selector.quote.exact });
    }

    // Clear when clicking outside the container (e.g. closing popover)
    function onPointerDown(e: PointerEvent) {
      if (!container!.contains(e.target as Node)) {
        setCaptured(null);
      }
    }

    container.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      container.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [containerRef]);

  return { captured, clear };
}
